import axios, { AxiosResponse } from 'axios';
import { apiRoute } from '../constants/api-constants';
import {
  AccessGrantCreated,
  BuildingPortfolioMove,
  BuildingRoster,
  CreateAccessGrantInput,
  CreateOccupancyInput,
  CreatePortfolioInput,
  CreateUnitInput,
  GrantRevokeResponse,
  ListUnitsOptions,
  MessageResponse,
  MoveOutInput,
  Occupancy,
  OccupancyPassesOutcome,
  Portfolio,
  PortfolioBuildingIndexRow,
  PortfolioBuildingsMoved,
  PortfolioDetail,
  PortfolioStatusFilter,
  ScopeSummary,
  StaffScopes,
  Unit,
  UnitDeleteResponse,
  UnitDetail,
  UnitListItem,
  UnitOccupancyHistory,
  UpdateOccupancyInput,
  UpdatePortfolioInput,
  UpdateUnitInput,
} from '../interfaces/hierarchy-interfaces';

// Typed wrapper over the Organization -> Portfolio -> Building -> Unit API.
//
// Uses the DEFAULT axios instance on purpose, so every call inherits the
// interceptors installed in axios-setup.ts: withCredentials for the httpOnly
// session cookies, the X-CSRF-Token double-submit header, and the single-flight
// 401 -> refresh -> retry. Creating a fresh instance here (as public-client.ts
// does for pre-auth calls) would silently opt every one of these authenticated
// calls out of session refresh.
//
// apiRoute already carries the /api prefix.

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

// The documented 409 outcomes, as codes the UI can branch on.
//
// These exist so a screen can react structurally — offer "move the buildings
// first", or swap a Delete button for Archive — not so it can invent its own
// wording. The server's message is always more specific than a code can be (it
// carries the actual building count, the colliding unit number, the date the
// occupancy closed), so HierarchyApiError.message is the string to display and
// this is the string to branch on.
export type HierarchyConflict =
  | 'portfolio_name_taken'
  | 'portfolio_is_default'
  | 'portfolio_has_buildings'
  | 'portfolio_is_archived'
  | 'unit_number_taken'
  | 'unit_has_occupants'
  | 'unit_has_history'
  | 'occupancy_open_duplicate'
  | 'occupancy_primary_taken'
  | 'occupancy_already_closed'
  | 'grant_already_open'
  | 'unknown_conflict';

export class HierarchyApiError extends Error {
  // null when the request never reached the server (offline, DNS, CORS).
  readonly status: number | null;

  // Set only on a 409. Every other failure leaves this null, so `if
  // (error.conflict)` is a safe stand-in for "this is a rule violation the user
  // can act on" rather than a transport or permission failure.
  readonly conflict: HierarchyConflict | null;

  // POST /staff/:userId/scopes returns the id of the grant that already covers
  // the requested scope, so the UI can link to it instead of just refusing.
  readonly grantId: string | null;

  constructor(
    message: string,
    status: number | null,
    conflict: HierarchyConflict | null,
    grantId: string | null
  ) {
    super(message);
    this.name = 'HierarchyApiError';
    this.status = status;
    this.conflict = conflict;
    this.grantId = grantId;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }

  // 403 from these routers means "outside your building scope", which is a
  // different message from "you lack the permission" — worth distinguishing.
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

// Matched against the message the router actually sends. Substrings are chosen
// to be the invariant part of each string — the counts, unit numbers and dates
// the server interpolates are deliberately outside every pattern.
const CONFLICT_PATTERNS: Record<
  Exclude<HierarchyConflict, 'unknown_conflict'>,
  RegExp
> = {
  // Two wordings: the create/rename collision, and the restore collision where
  // another portfolio took the name while this one was archived (uq_pf_name is
  // partial on status='active', so archiving frees the name immediately).
  portfolio_name_taken:
    /(portfolio with this name already exists|another portfolio is now called)/i,
  portfolio_is_default: /default portfolio cannot be (deleted|archived)/i,
  portfolio_has_buildings: /still contains/i,
  // Covers "This portfolio is archived…", "That portfolio is archived…" and
  // "An archived portfolio cannot be made the default". Broad on purpose: the
  // candidate list is scoped per call, so it can only ever match on an endpoint
  // that documents this outcome.
  portfolio_is_archived: /archived/i,
  unit_number_taken: /already exists in this building/i,
  unit_has_occupants: /current occupant/i,
  unit_has_history: /occupancy (record|history)/i,
  occupancy_open_duplicate: /open occupancy on this unit/i,
  occupancy_primary_taken: /open primary occupant/i,
  occupancy_already_closed: /already closed on/i,
  grant_already_open: /open grant for that scope/i,
};

// Which conflicts a given endpoint can raise, most-likely first. Passing the
// candidate list per call rather than matching against all ten patterns keeps a
// message from being mapped to a conflict its endpoint cannot produce.
const classifyConflict = (
  candidates: readonly HierarchyConflict[],
  message: string | null
): HierarchyConflict => {
  // An endpoint with exactly one documented 409 needs no message sniffing.
  if (candidates.length === 1) {
    return candidates[0];
  }

  if (message) {
    const matched = candidates.find(
      candidate =>
        candidate !== 'unknown_conflict' &&
        CONFLICT_PATTERNS[candidate].test(message)
    );

    if (matched) {
      return matched;
    }
  }

  return 'unknown_conflict';
};

// Every router in this group answers errors as { message: string }. Read
// defensively anyway: a proxy or a crash upstream can return HTML or nothing at
// all, and `data.message` on a string body is undefined, not a throw.
const readBody = (
  data: unknown
): { message: string | null; grantId: string | null } => {
  if (!data || typeof data !== 'object') {
    return { message: null, grantId: null };
  }

  const body = data as { message?: unknown; grant_id?: unknown };
  const message =
    typeof body.message === 'string' && body.message.trim()
      ? body.message
      : null;
  const grantId = typeof body.grant_id === 'string' ? body.grant_id : null;

  return { message, grantId };
};

const toHierarchyError = (
  error: unknown,
  candidates: readonly HierarchyConflict[]
): HierarchyApiError => {
  if (!axios.isAxiosError(error)) {
    const message =
      error instanceof Error ? error.message : 'Something went wrong';

    return new HierarchyApiError(message, null, null, null);
  }

  const status = error.response?.status ?? null;
  const { message, grantId } = readBody(error.response?.data);

  // The server's own wording wins. Falling through to axios's generic
  // "Request failed with status code 409" is the exact failure this module
  // exists to prevent.
  const text =
    message ||
    (status === null
      ? 'Could not reach the server. Check your connection and try again.'
      : error.message);

  const conflict =
    status === 409 ? classifyConflict(candidates, message) : null;

  return new HierarchyApiError(text, status, conflict, grantId);
};

const request = async <T>(
  send: () => Promise<AxiosResponse<T>>,
  candidates: readonly HierarchyConflict[] = []
): Promise<T> => {
  try {
    const response = await send();

    return response.data;
  } catch (error) {
    throw toHierarchyError(error, candidates);
  }
};

// ---------------------------------------------------------------------------
// Portfolios
// ---------------------------------------------------------------------------

// Defaults to ACTIVE portfolios only — that is also the server default, so a
// picker that just calls listPortfolios() can never offer an archived one. Pass
// 'all' where Restore has to be reachable (the Portfolios screen).
export const listPortfolios = (
  status: PortfolioStatusFilter = 'active'
): Promise<Portfolio[]> =>
  request(() =>
    axios.get<Portfolio[]>(`${apiRoute}/portfolios`, { params: { status } })
  );

export const getPortfolio = (id: number): Promise<PortfolioDetail> =>
  request(() => axios.get<PortfolioDetail>(`${apiRoute}/portfolios/${id}`));

// Every building in the org with the portfolio holding it, in ONE request.
// Replaces getPortfolio()-per-portfolio in the pickers that need to group
// buildings by portfolio.
export const listPortfolioBuildings = (): Promise<
  PortfolioBuildingIndexRow[]
> =>
  request(() =>
    axios.get<PortfolioBuildingIndexRow[]>(`${apiRoute}/portfolios/buildings`)
  );

export const createPortfolio = (
  input: CreatePortfolioInput
): Promise<Portfolio> =>
  request(
    () => axios.post<Portfolio>(`${apiRoute}/portfolios`, input),
    ['portfolio_name_taken']
  );

export const updatePortfolio = (
  id: number,
  input: UpdatePortfolioInput
): Promise<Portfolio> =>
  request(
    () => axios.put<Portfolio>(`${apiRoute}/portfolios/${id}`, input),
    ['portfolio_name_taken']
  );

// 409 in two flavours the UI must not conflate: the default portfolio can NEVER
// be deleted (hide the action), whereas a portfolio holding buildings becomes
// deletable once they are moved (offer that).
export const deletePortfolio = (id: number): Promise<MessageResponse> =>
  request(
    () => axios.delete<MessageResponse>(`${apiRoute}/portfolios/${id}`),
    ['portfolio_is_default', 'portfolio_has_buildings']
  );

// ---------------------------------------------------------------------------
// Portfolio membership — which portfolio a building belongs to
//
// A building belongs to exactly ONE portfolio and the column is NOT NULL, so
// there is no "remove from portfolio": removal is a move somewhere else, and
// the org's default portfolio is where things go by default. These two calls
// are that single move, addressed from each end.
// ---------------------------------------------------------------------------

// Move one building. Returns moved:false (not an error) when it was already
// there, so a UI can stay quiet instead of claiming it did something.
export const moveBuildingToPortfolio = (
  buildingId: number,
  portfolioId: number
): Promise<BuildingPortfolioMove> =>
  request(
    () =>
      axios.put<BuildingPortfolioMove>(
        `${apiRoute}/buildings/${buildingId}/portfolio`,
        { portfolio_id: portfolioId }
      ),
    ['portfolio_is_archived']
  );

// Move many buildings INTO a portfolio, atomically — either all of them land or
// none does. Capped server-side at 100 per call.
export const addBuildingsToPortfolio = (
  portfolioId: number,
  buildingIds: number[]
): Promise<PortfolioBuildingsMoved> =>
  request(
    () =>
      axios.post<PortfolioBuildingsMoved>(
        `${apiRoute}/portfolios/${portfolioId}/buildings`,
        { building_ids: buildingIds }
      ),
    ['portfolio_is_archived']
  );

// ---------------------------------------------------------------------------
// Portfolio lifecycle
// ---------------------------------------------------------------------------

// Archive is reversible and only legal for an EMPTY, non-default portfolio:
// archiving frees the name for reuse, so a portfolio left holding buildings
// would keep granting staff access under a name something else had taken.
export const archivePortfolio = (id: number): Promise<Portfolio> =>
  request(
    () => axios.post<Portfolio>(`${apiRoute}/portfolios/${id}/archive`),
    ['portfolio_is_default', 'portfolio_has_buildings']
  );

// Can collide: the name was released while this portfolio was archived.
export const unarchivePortfolio = (id: number): Promise<Portfolio> =>
  request(
    () => axios.post<Portfolio>(`${apiRoute}/portfolios/${id}/unarchive`),
    ['portfolio_name_taken']
  );

// Make this the portfolio a building lands in when none is chosen. Also the one
// portfolio that can never be deleted, which is how the previous default
// becomes deletable.
export const setDefaultPortfolio = (id: number): Promise<Portfolio> =>
  request(
    () => axios.post<Portfolio>(`${apiRoute}/portfolios/${id}/default`),
    ['portfolio_is_archived']
  );

// ---------------------------------------------------------------------------
// Units
// ---------------------------------------------------------------------------

// The backend compares these against the literal string 'true', so the flags are
// only sent when set — an omitted param and `false` are the same thing to it.
export const listUnits = (
  buildingId: number,
  options: ListUnitsOptions = {}
): Promise<UnitListItem[]> => {
  const params: Record<string, string> = {};

  if (options.vacant) {
    params.vacant = 'true';
  }

  if (options.includeArchived) {
    params.include_archived = 'true';
  }

  if (options.kind) {
    params.kind = options.kind;
  }

  return request(() =>
    axios.get<UnitListItem[]>(`${apiRoute}/buildings/${buildingId}/units`, {
      params,
    })
  );
};

/**
 * EVERY UNIT THE CALLER CAN SEE, across every building — one request.
 *
 * `listUnits` above is per building, which is right for a screen that already
 * has one. The report filter does not: its whole point is that a building is
 * optional, so populating its Apartment/Location select from the per-building
 * route would mean one request per building before the drawer could open.
 *
 * The scoping is the server's and is not a parameter here: `units` carries RLS,
 * so a manager receives the buildings their grants cover and an owner receives
 * all of them, from the same call.
 */
export interface OrgUnitOption {
  id: number;
  building_id: number;
  unit_number: string;
  kind: string;
  is_archived: boolean;
}

export const listAllUnits = (): Promise<OrgUnitOption[]> =>
  request(() => axios.get<OrgUnitOption[]>(`${apiRoute}/dashboard/units`));

export const getUnit = (id: number): Promise<UnitDetail> =>
  request(() => axios.get<UnitDetail>(`${apiRoute}/units/${id}`));

export const createUnit = (
  buildingId: number,
  input: CreateUnitInput
): Promise<Unit> =>
  request(
    () => axios.post<Unit>(`${apiRoute}/buildings/${buildingId}/units`, input),
    ['unit_number_taken']
  );

// Archiving a unit someone still lives in is refused, so a status change and a
// rename can both 409 from the same call.
export const updateUnit = (id: number, input: UpdateUnitInput): Promise<Unit> =>
  request(
    () => axios.put<Unit>(`${apiRoute}/units/${id}`, input),
    ['unit_has_occupants', 'unit_number_taken', 'unit_has_history']
  );

// ONE VERB (R-20). Call this with no options; the server resolves whether the
// row can go or has to be retained as archived, and answers "Unit deleted"
// either way. That is not a euphemism: fk_occ_unit is ON DELETE RESTRICT, so a
// unit that has ever been lived in cannot be row-deleted, and a product that
// offered only the row delete would leave every once-occupied unit permanently
// unremovable.
//
// `archive` is an INTERNAL escape hatch for an explicit archive, kept for
// ops/API use. No control in the product passes it — offering it would be the
// second destructive button R-20 removes.
//
// Only one 409 is reachable now: somebody still lives there. unit_has_history
// used to arrive here and cannot any more, because the delete path absorbs it.
export const deleteUnit = (
  id: number,
  options: { archive?: boolean } = {}
): Promise<UnitDeleteResponse> =>
  request(
    () =>
      axios.delete<UnitDeleteResponse>(`${apiRoute}/units/${id}`, {
        params: options.archive ? { archive: 'true' } : {},
      }),
    ['unit_has_occupants']
  );

// ---------------------------------------------------------------------------
// Occupancies
// ---------------------------------------------------------------------------

// Full history for a unit, newest first, closed stays included — render it as a
// timeline rather than filtering to the current occupants.
export const listUnitOccupancies = (
  unitId: number
): Promise<UnitOccupancyHistory> =>
  request(() =>
    axios.get<UnitOccupancyHistory>(`${apiRoute}/units/${unitId}/occupancies`)
  );

// WHO'S HOME — both halves in ONE call, deliberately.
//
// The response carries TWO SECTIONS: `residents` (open occupancies) and
// `visitors_inside` (arrivals with no departure). Fetching them separately
// would let the screen show a visitor who was signed out between the two
// requests, so the server reads both inside one transaction and stamps the one
// instant on `as_of`.
//
// `/presence` is the endpoint's honest name now that it is no longer
// resident-only; `/buildings/:id/roster` is the same handler under the older
// name and still answers, so nothing that predates this breaks.
//
// `tz` IS THE READER'S OWN ZONE, and it is only ever used as a fallback. The
// building's zone wins whenever it has one (a shift belongs to the building's
// clock, whoever is reading it) — but a building that has never had one set
// used to render this whole screen on the Greenwich clock, because the column
// DEFAULTS to 'UTC' and the server could not tell that default from a choice.
// Sending the browser's zone gives it something better than Greenwich to fall
// back to; `timezone_source` on the response says which one it used.
export const getBuildingRoster = (
  buildingId: number
): Promise<BuildingRoster> =>
  request(() =>
    axios.get<BuildingRoster>(`${apiRoute}/buildings/${buildingId}/presence`)
  );

// Move-in. Passing is_primary: true DISPLACES the unit's current primary
// occupant in the same transaction; leaving it undefined claims the slot only if
// it is free, which is why a bare create never 409s on the primary constraint.
export const createOccupancy = (
  unitId: number,
  input: CreateOccupancyInput
): Promise<Occupancy> =>
  request(
    () =>
      axios.post<Occupancy>(`${apiRoute}/units/${unitId}/occupancies`, input),
    ['occupancy_open_duplicate', 'occupancy_primary_taken']
  );

// Corrections only. Sending unit_id or building_id is a 400, not a conflict:
// relocation is a move-out followed by a new occupancy on the target unit.
export const updateOccupancy = (
  id: string,
  input: UpdateOccupancyInput
): Promise<Occupancy> =>
  request(
    () =>
      axios.put<Occupancy>(
        `${apiRoute}/occupancies/${encodeURIComponent(id)}`,
        input
      ),
    ['occupancy_primary_taken', 'occupancy_open_duplicate']
  );

// Sets moved_out; the row is never deleted. Re-closing an already-closed
// occupancy is a 409 pointing at updateOccupancy, since re-dating a completed
// move-out is a correction rather than a repeat of this action.
// `passes` (Plan 7): what happened to the visitor passes the resident hosted;
// null when the server could not work it out.
export const moveOutOccupancy = (
  id: string,
  input: MoveOutInput = {}
): Promise<Occupancy & { passes?: OccupancyPassesOutcome | null }> =>
  request(
    () =>
      axios.post<Occupancy & { passes?: OccupancyPassesOutcome | null }>(
        `${apiRoute}/occupancies/${encodeURIComponent(id)}/move-out`,
        input
      ),
    ['occupancy_already_closed']
  );

// ---------------------------------------------------------------------------
// Staff scopes
// ---------------------------------------------------------------------------

// Every staff member with their grant and resolved-building counts. A staff
// member on 0 buildings can sign in and see nothing — surface that.
export const getScopeSummary = (): Promise<ScopeSummary[]> =>
  request(() => axios.get<ScopeSummary[]>(`${apiRoute}/staff/scopes/summary`));

export const getStaffScopes = (userId: string): Promise<StaffScopes> =>
  request(() =>
    axios.get<StaffScopes>(
      `${apiRoute}/staff/${encodeURIComponent(userId)}/scopes`
    )
  );

// Supply the one id column matching scope_type and omit the others; sending an
// extra one is a 400. The response reports how many buildings the grant actually
// resolved to, which is the number to show back to the admin.
export const grantStaffScope = (
  userId: string,
  input: CreateAccessGrantInput
): Promise<AccessGrantCreated> =>
  request(
    () =>
      axios.post<AccessGrantCreated>(
        `${apiRoute}/staff/${encodeURIComponent(userId)}/scopes`,
        input
      ),
    ['grant_already_open']
  );

// Takes effect immediately: the trigger drops the grant's access_scope rows on
// delete. The audit log, not this table, is what preserves the history.
export const revokeStaffScope = (
  grantId: string
): Promise<GrantRevokeResponse> =>
  request(() =>
    axios.delete<GrantRevokeResponse>(
      `${apiRoute}/staff/scopes/${encodeURIComponent(grantId)}`
    )
  );
